import React, { useState, useEffect, useRef } from "react";

const CREATIVE_PROMPTS = [
  "If your mood were a weather system, describe it in detail.",
  "Write about a colour that matches how you feel right now.",
  "Describe a place you've never been that you feel drawn to.",
  "What would you say to yourself six months ago?",
  "Write the opening line of a story about someone like you.",
  "If today were a texture, what would it feel like?",
];

const DEFAULT_DURATION = 180; // 3 minutes

function formatTime(s) {
  const m = Math.floor(s / 60);
  return `${m}:${(s % 60).toString().padStart(2, "0")}`;
}

// Picks a random prompt from the list, avoiding the last one shown
let lastPromptIndex = -1;
function pickPrompt() {
  let idx;
  do { idx = Math.floor(Math.random() * CREATIVE_PROMPTS.length); }
  while (idx === lastPromptIndex && CREATIVE_PROMPTS.length > 1);
  lastPromptIndex = idx;
  return CREATIVE_PROMPTS[idx];
}

export default function CreativeExpressionTemplate({ config = {}, onProgress, onComplete }) {
  const duration = config.duration || DEFAULT_DURATION;
  const [prompt] = useState(() => config.prompt || pickPrompt());

  const [text, setText]         = useState("");
  const [timeLeft, setTimeLeft] = useState(duration);
  const [started, setStarted]   = useState(false);
  const completedRef            = useRef(false);
  const timeRef                 = useRef(duration);

  // Auto-start timer on first keystroke (research: removing friction enables divergent thinking)
  const handleChange = (e) => {
    setText(e.target.value);
    if (!started) setStarted(true);
  };

  useEffect(() => {
    if (!started) return;
    const interval = setInterval(() => {
      const next = Math.max(timeRef.current - 1, 0);
      timeRef.current = next;
      setTimeLeft(next);
      onProgress?.(1 - next / duration, formatTime(next) + " remaining");
      if (next <= 0 && !completedRef.current) {
        completedRef.current = true;
        onComplete?.();
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [started]); // eslint-disable-line

  const isTimerDone = timeLeft <= 0;

  return (
    <div className="tw-flex tw-flex-col tw-items-center tw-w-full tw-gap-4">
      {/* Prompt */}
      <p
        className="tw-text-xl tw-font-light tw-italic tw-text-center tw-max-w-[360px] tw-leading-relaxed"
        style={{ color: "rgba(255,255,255,0.85)" }}
      >
        {prompt}
      </p>

      {/* Textarea */}
      <textarea
        value={text}
        onChange={handleChange}
        placeholder="Start writing — the timer begins when you do..."
        disabled={isTimerDone}
        className="tw-w-full tw-rounded-2xl tw-text-[15px] tw-leading-[1.7] tw-resize-none tw-outline-none tw-transition-all tw-duration-300"
        style={{
          height: 180,
          background: "rgba(255,255,255,0.07)",
          border: "1px solid rgba(255,255,255,0.12)",
          padding: "18px",
          color: "rgba(255,255,255,0.85)",
          fontFamily: "inherit",
          maxWidth: 420,
        }}
        onFocus={(e) => {
          e.target.style.borderColor = "rgba(160,140,255,0.6)";
          e.target.style.boxShadow   = "0 0 0 3px rgba(91,63,214,0.2)";
        }}
        onBlur={(e) => {
          e.target.style.borderColor = "rgba(255,255,255,0.12)";
          e.target.style.boxShadow   = "none";
        }}
      />

      {/* Footer: timer + hint */}
      <div className="tw-flex tw-justify-between tw-items-center tw-w-full" style={{ maxWidth: 420 }}>
        <p className="tw-text-xs tw-tracking-[0.5px]" style={{ color: "rgba(255,255,255,0.4)" }}>
          {started
            ? isTimerDone ? "Time's up — keep going if you'd like." : formatTime(timeLeft)
            : "Timer starts when you begin writing"}
        </p>
        {!started && (
          <p className="tw-text-xs" style={{ color: "rgba(255,255,255,0.35)" }}>
            No word count · no judgment
          </p>
        )}
      </div>
    </div>
  );
}
